// external import
import { DataTable, DataTableColReorderParams, DataTableColumnResizeEndParams, DataTablePageParams } from "primereact/datatable";
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect, ReactElement, forwardRef } from "react";
import { ContextMenu } from "primereact/contextmenu";
import { useTranslation } from "react-i18next";
import classnames from "classnames/dedupe";
import { useSelector } from "react-redux";

// internal import
import { ResponsiveButtonsContainer } from "../../responsive-buttons-wrapper/intersectionButtons";
import { AdvanceFiltersWrapper } from "../components/pop-ups/advanceFilters/advance-filters";
import { AdvancedRemarksPopup } from "../components/pop-ups/advanced-remarks-popup";
import { AdvancedRemarksContextProvider } from "../advancedRemarksContext/context";
import { IFilter } from "../components/pop-ups/advanceFilters/interfaces";
import { LoadingMessage } from "../components/messages/loading-message";
import { EmptyMessage } from "../components/messages/empty-message";
import { MemoRowGroupHeader } from "../components/row-group-header";
import { useTableScrollHeight } from "hooks/useTableScrollHeight";
import { tablePaginator } from "../components/table-paginator";
import { sweetConfirm } from "../../sweet-alert/sweetConfirm";
import { applySubmit } from "../helpers/applySubmit";
import { getColumnToGroupBy } from "../helpers";
import { deepCopy } from "utils/memo/deepCopy";

// table components
import { PrimeContextMenu } from "../components/contextMenu/context-menu";

import { useDeepCompareEffect } from "hooks"; // hooks

// interfaces
import * as FI from "../interfaces/filter-interfaces"; // internal filters interfaces import
import * as I from "../interfaces"; // internal table interfaces import
import * as H from "../helpers"; // helpers import

// internal style import
import "./../style.scss";

// TODO! wyświetlanie odpowiednich filtrów i input'ów w additionalColumns (flaga że kolumna jest additional?????)
// one record in tbody should have minimum 31px, if this changes you have to update this variable.
const RECORDS_HEIGHT: number = 31;

const PrimeDataTableInner = <T extends I.IBaseRecord = I.IBaseRecord>(props: I.IPrimeDataTable<T>) => {
    const {
        dataSet = [],
        setData,
        columns,
        tableName,
        dynamicColumns = false,
        // start visual settings section
        className = "",
        children,
        btnContainerClassName,
        showGridlines = true,
        reorderColumnsEnabled = true,
        resizableColumnsEnabled = true,
        minimumColWidth = 123, // 123 is a perfect number for table to scalable in normal and scroll mode
        disableHeadersTranslation = false,
        isLoading,
        isPending,
        adjustTableHeight = 0,
        getContainerHeight = true,
        rowClassName,
        virtualScroll,
        // end visual settings section

        // start pagination section
        meta,
        onReload,
        rowsPerPage = [5, 10, 15, 25, 50, 100],
        // end pagination section
        // start select record section
        selectedRecords = [],
        onSelect = undefined,
        notSelectFirst = false,
        selectionMode = "multiple",
        idSelector = "id",
        disableAdditionalColumns,
        metaKeySelection = true,
        // end select record section

        // start sorting and grouping section
        sortable = true,
        higherDependencyId = [],
        onParamsChange,
        groupRowsEnabled = true,
        customRowGroupHeader = undefined,
        customRowGroupFooter = undefined,
        useFetchTableData = false,
        // end sorting and grouping section

        // start advanced filter section
        advancedFiltersConfig = undefined,
        // end advanced filter section

        // start context menu section, if any prop below is enabled or has correct data, then context menu will be enabled and context menu item will show when right clicked on table
        auditData = undefined,
        exportCSVEnabled = false,
        hidingColumnEnabled = true,
        filterEnabled = true,
        frozenColumnEnabled = true,
        translateDisabled = false,
        contextMenuItems = [],
        onContextColumnFieldChange = undefined,
        // end context menu section

        handleDoubleClick = undefined,

        // start edit section
        onEdit = undefined,
        handleCustomResponse = undefined,
        extraEditParams = {},
        firstPartEditUrl = "",
        refreshCallBackFunctions: refreshCallBack = [],
        customHandleEditSubmit = undefined,
        hideSuccessToast = false,
        selectChoices,
        // start edit section

        roleFilter = false,
        extraContent,
        externalColumnFilters = undefined,
        externalCustomParam,
        shouldUseExternalFilters = true,
        advancedRemarksModel = meta?.ModelInfo?.model,
    } = props;
    const { t } = useTranslation();

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const contextMenuRef = useRef<ContextMenu>(null);

    const { isRefreshing } = useSelector((state) => ({ isRefreshing: state.serverConnection.isRefreshing }));

    const [filterFocusedFieldName, setFilterFocusedFieldName] = useState<{ fieldName: string; tableName: string }>({
        fieldName: "",
        tableName: "",
    }); // handle input field name for solving focus loss problem

    // Meta data

    const paginationMeta = {
        first: meta?.page ? (meta.page < 2 ? 0 : (meta.page - 1) * meta.pageSize) : 0,
        rows: meta?.pageSize,
        page: meta?.page || 1,
        totalRecords: meta?.count,
    };

    // Param data, this variable stores data about filters, sort and grouping
    const [params, setParams] = useState<I.IPrimeParams>({ filters: [], multiSortMeta: undefined });

    // Column Data, this variable stores data about currently visible columns and default(but translated) columns
    const [generatedColumns, setGeneratedColumns] = useState<I.IGeneratedPrimeColumns>({
        visible: [],
        default: [],
        columnGenerateStatus: "NOT_GENERATED_COLUMNS",
    });

    const [tableConfig, setTableConfig] = useState<I.ITableConfig>({
        contextItemFilterEnable: false,
        filtersEnabled: true,
        frozenColumn: undefined,
        tableHeight: 155,
    });

    const [scrollConfig, setScrollConfig] = useState<I.IScrollConfig | undefined>(undefined);
    const [isBusy, setIsBusy] = useState<boolean>(false);
    const [advancedFilterVisible, setAdvancedFilterVisible] = useState(false);

    const toggleAdvancedFilters = () => setAdvancedFilterVisible((prev) => !prev);
    const handleCloseAdvancedFilters = () => setAdvancedFilterVisible(false);

    const additionalColumnsEnabled: boolean = useMemo(() => {
        if (disableAdditionalColumns) return !disableAdditionalColumns;
        else return !!meta?.ExtraFields;
    }, [!!meta?.ExtraFields]);

    //! USEMEMO SECTION START
    const tableEnabledFunctionality: I.ITableEnabledFunctionality = useMemo(() => {
        const _sortable: boolean = !!onReload && sortable;

        return {
            pagination: !!onReload && !!paginationMeta && !!paginationMeta.rows,
            groupingEnabled: _sortable && groupRowsEnabled,
            groupingSelected: _sortable && groupRowsEnabled && !!params.multiSortMeta?.[0]?.groupByThisField,
            sortable: _sortable,
        };
    }, [onReload, sortable, !!paginationMeta, paginationMeta.rows, params?.multiSortMeta, groupRowsEnabled]);

    const tableClassName: string = useMemo((): string => {
        const frozenColumnClass = tableConfig.frozenColumn ? "frozen-mode" : "";
        const verticalScrollClass = `vertical-scroll ${scrollConfig?.vertical ? "enabled" : "disabled"}`;
        const disableLastColResize = !scrollConfig?.horizontal ? "disable-last-col-resize" : "";
        const filtersEnabled = tableConfig.filtersEnabled ? "filters-enabled" : "";

        return `${frozenColumnClass} ${verticalScrollClass} ${disableLastColResize} ${filtersEnabled}`;
    }, [tableConfig.frozenColumn, tableConfig.filtersEnabled, scrollConfig]);
    //! USEMEMO SECTION STOP

    // SELECTING
    useDeepCompareEffect(() => {
        // Empty selected records if table is empty
        if (!dataSet.length && onSelect) onSelect([]);
        // try to select record after editing record
        else if (tableContainerRef.current && onSelect && !!metaKeySelection) {
            if (H.mapToIds(dataSet).includes(selectedRecords[0]?.[idSelector])) {
                const selectedRecordsAfterEditing = selectedRecords.map(
                    (record) => dataSet.find((dataSetRecord) => dataSetRecord[idSelector] == record[idSelector]) || record
                );

                onSelect(selectedRecordsAfterEditing);
                //this handle deleting
            } else onSelect([dataSet[0]]);
        }
    }, [dataSet]);

    // this handles changing pages, adding new record selected record
    useEffect(() => {
        if (onSelect && !notSelectFirst && tableContainerRef.current && dataSet[0]?.[idSelector]) {
            onSelect([dataSet[0]]);
        }
    }, [dataSet[0]?.id, dataSet.length]);
    // END SELECTING

    useEffect(() => {
        scrollConfig && handleScrollConfig("vertical");
    }, [dataSet.length]);

    useDeepCompareEffect(() => {
        if (generatedColumns.columnGenerateStatus != "GENERATED_COLUMNS") handleTableColumns();
    }, [meta]);

    useDeepCompareEffect(() => {
        if (generatedColumns.columnGenerateStatus != "NOT_GENERATED_COLUMNS") handleTableColumns();
    }, [columns]);

    useDeepCompareEffect(() => {
        // checks which scrolls should be enabled, on init when visible columns are already generated, and when visible columns changes
        if (generatedColumns.visible && generatedColumns.visible.length > 0) handleScrollConfig("default");
    }, [generatedColumns.visible]);

    useDeepCompareEffect(() => {
        // this event listener has to be added every time when visible columns changes
        if (generatedColumns.visible && generatedColumns.visible.length > 0)
            window.addEventListener("resize", () => handleScrollConfig("horizontal"));
        return () => window.removeEventListener("resize", () => handleScrollConfig("horizontal"));
    }, [scrollConfig]);

    useDeepCompareEffect(() => {
        if (params.multiSortMeta && params.multiSortMeta[0].groupByThisField) {
            setParams({ filters: [], multiSortMeta: [params.multiSortMeta[0]] });
        } else if (!!params.filters || !!params.multiSortMeta) {
            setParams({ filters: [], multiSortMeta: undefined });
        }
    }, [higherDependencyId]);

    useLayoutEffect(() => {
        //! do usunięcia jak frozen col będzie zrobiony i wiersze nie będą możliwe do rozwijania
        const dataSetLength = dataSet?.length;

        if (dataSetLength > 0 && dataSetLength <= H.getDataSetBreakpoint(tableConfig.tableHeight, RECORDS_HEIGHT)) {
            if (scrollConfig && !scrollConfig?.vertical) {
                const verticalScroll = H.isVerticalScrollEnabled(tableContainerRef, tableConfig.tableHeight, tableConfig.filtersEnabled);

                verticalScroll && setScrollConfig({ vertical: true, horizontal: scrollConfig?.horizontal });
            }
        }
    }, []);

    // set filters from out of component
    useDeepCompareEffect(() => {
        if (externalColumnFilters && shouldUseExternalFilters) {
            setParams((prev) => ({
                ...prev,
                filters: externalColumnFilters,
            }));
            handleApplyAdvancedFilters(externalColumnFilters, externalCustomParam);
        }
    }, [externalColumnFilters, externalCustomParam, shouldUseExternalFilters]);

    // generates request for saveTableConfig function and saves new config
    const handleSaveTableConfig = async (action: I.ListActionType) => {
        let request: I.IUserTableConfig = {
            columns: generatedColumns.visible,
            filtersEnabled: tableConfig.filtersEnabled,
        };

        switch (action.type) {
            case "columns":
                request.columns = action.payload.columns;
                break;
            case "filterEnabled":
                request.filtersEnabled = action.payload.filterEnabled;
                break;
        }

        await H.saveTableConfig(tableName, request);
    };

    // Configuring table options based on columns and user saved table config
    const handleTableColumns = async () => {
        const additionalColumnsPromise: Promise<I.IPrimeTableColumn[]> = H.mapToAdditionalColumns(
            meta?.ExtraFields,
            additionalColumnsEnabled
        );

        let _columns: I.IGeneratedPrimeTableColumn[];
        // Generating default columns

        let defaultColumns: I.IGeneratedPrimeTableColumn[];
        let tableConfigResponse: I.IUserTableConfig | undefined = undefined;

        // Fetching columns, validating fetched columns with generated default columns, and if fetched columns exist, mapping default columns to user saved columns
        if (!dynamicColumns) {
            const tableConfigPromise = H.getTableConfig(tableName);

            const [tableConfigResponse, additionalColumns] = await Promise.all([tableConfigPromise, additionalColumnsPromise]);

            _columns = H.mapColumnsOnInit(
                tableContainerRef,
                [...columns, ...additionalColumns],
                !disableHeadersTranslation ? t : undefined,
                minimumColWidth
            );

            defaultColumns = _columns;

            if (tableConfigResponse && tableConfigResponse?.columns?.length > 0) {
                _columns = await H.compareColumns(
                    tableConfigResponse.columns,
                    defaultColumns,
                    meta === undefined ? undefined : () => handleSaveTableConfig({ type: "columns", payload: { columns: defaultColumns } })
                );
            } else if (_columns.length > 0) {
                // saving generated columns in DB if table config does not exist
                meta === undefined && handleSaveTableConfig({ type: "columns", payload: { columns: defaultColumns } });
            }
        } else {
            const additionalColumns: I.IPrimeTableColumn[] = await Promise.resolve(additionalColumnsPromise);

            _columns = H.mapColumnsOnInit(
                tableContainerRef,
                [...(columns || []), ...(additionalColumns || [])],
                !disableHeadersTranslation ? t : undefined,
                minimumColWidth
            );
            defaultColumns = _columns;
        }

        // saving visible columns in state
        setGeneratedColumns({
            visible: _columns,
            default: defaultColumns,
            columnGenerateStatus: meta === undefined ? "PLACEHOLDER_COLUMNS" : "GENERATED_COLUMNS",
        });

        // configure rest of the table settings
        handleTableConfig(_columns, tableConfigResponse);
    };

    const handleTableConfig = (columns: I.IGeneratedPrimeTableColumn[], fetchedTableConfig?: I.IUserTableConfig) => {
        let filtersEnabled = false;
        let tableHeight = tableConfig.tableHeight;
        let groupedColumn: I.DataTableSortMeta[] | undefined;

        // checking if filter should be opened
        if (onReload) {
            filtersEnabled = fetchedTableConfig?.filtersEnabled ? fetchedTableConfig?.filtersEnabled : tableConfig.filtersEnabled;
        }

        // if filter is opened then we have to change tableHeight and scss variable --table-min-height
        if (filtersEnabled) {
            const filterHeight: number = parseInt(H.getCssVariable(tableContainerRef, "--header-height"));
            tableHeight = filterEnabled ? tableHeight - filterHeight : tableHeight + filterHeight;
        }

        let config: I.ITableConfig = { frozenColumn: undefined, contextItemFilterEnable: false, tableHeight, filtersEnabled };

        columns.forEach((col, index) => {
            // if there is even one filterBody in columns then context menu show/hide menu item will be enabled
            if (!!onReload && !!col.filterBody && !config.contextItemFilterEnable) {
                config.contextItemFilterEnable = true;
            }

            // checks if there is column that should be frozen on init, it also sets frozen col width
            if (col.frozen && !config.frozenColumn) {
                config.frozenColumn = {
                    field: col.field,
                    width: H.getFrozenColWidth(col),
                    originalPosition: index,
                };
                delete col.frozen;
            }

            // checks if there is column that should be grouped on init
            if (col.grouped && !groupedColumn) {
                groupedColumn = [{ header: col.header, field: col.field, order: 1, groupByThisField: true }];
                delete col.grouped;
            }
        });

        //if there is column that should be grouped on init then onSort function is fired
        if (groupedColumn) {
            handleSort({ multiSortMeta: groupedColumn }, true, columns);
            !useFetchTableData && onReload && onReload(undefined, true);
        }

        setTableConfig(config);
    };

    // Resize event listener, it checks if table should be in horizontal scroll mode or not
    const handleScrollConfig = (
        type: I.HandleScrollConfigType,
        tableHeight: number = tableConfig.tableHeight,
        filtersEnabled: boolean = tableConfig.filtersEnabled
    ) => {
        let _scrollConfig: I.IScrollConfig = {
            vertical: scrollConfig?.vertical || false,
            horizontal: scrollConfig?.horizontal || false,
        };

        switch (type) {
            case "horizontal":
                _scrollConfig.horizontal = H.isHorizontalScrollEnabled(tableContainerRef, generatedColumns.visible);

                break;
            case "vertical":
                _scrollConfig.vertical =
                    dataSet?.length > H.getDataSetBreakpoint(tableHeight, RECORDS_HEIGHT) ||
                    H.isVerticalScrollEnabled(tableContainerRef, tableHeight, filtersEnabled);

                break;
            default:
                _scrollConfig = {
                    ..._scrollConfig,
                    vertical:
                        dataSet?.length > H.getDataSetBreakpoint(tableHeight, RECORDS_HEIGHT) ||
                        H.isVerticalScrollEnabled(tableContainerRef, tableHeight, filtersEnabled),
                    horizontal: H.isHorizontalScrollEnabled(tableContainerRef, generatedColumns.visible),
                };
                break;
        }

        if (JSON.stringify(scrollConfig) != JSON.stringify(_scrollConfig)) setScrollConfig(_scrollConfig);
    };

    const handlePage = async (event: DataTablePageParams) => {
        // this if is necessary because onPage was called twice when changing pageSize,
        // once when pageSize was changed, and second time when meta with new pageSize came
        if (event.page + 1 == paginationMeta?.page && event.rows == paginationMeta.rows) return;
        const { filters, multiSortMeta } = params;

        const urlParams = H.generateUrlParams({
            page: event.page + 1,
            rows: event.rows,
            multiSortMeta: mapMultiSortMeta(multiSortMeta),
            filters,
            additionalParams: externalCustomParam,
        });

        if (onReload) {
            onReload(urlParams);
        }
    };

    const handleSort = async (event, group: boolean = false, columns: I.IGeneratedPrimeTableColumn[] | undefined = undefined) => {
        const paramsSort = params.multiSortMeta;
        let eventSort: any | undefined = event.multiSortMeta;

        if (eventSort?.length == 0) eventSort = undefined;
        else if (!group && paramsSort?.[0].groupByThisField && eventSort[0].order != paramsSort[0].order) {
            eventSort = eventSort.map((s, index) => (index == 0 ? { ...paramsSort[0], order: eventSort[0].order } : s));
        }

        const urlParams = H.generateUrlParams({
            rows: paginationMeta?.rows,
            multiSortMeta: mapMultiSortMeta(eventSort, columns),
            filters: params.filters,
            additionalParams: externalCustomParam,
        });

        setParams({ ...params, multiSortMeta: eventSort });

        onReload && onReload(urlParams, undefined); // triggers dataReload
        onParamsChange && onParamsChange({ ...params, multiSortMeta: eventSort });
    };

    const mapMultiSortMeta = (
        multiSortMeta?: I.DataTableMultiSortMetaType,
        columns: I.IGeneratedPrimeTableColumn[] = generatedColumns.visible
    ) => {
        if (!multiSortMeta) return multiSortMeta;
        const filteredVisibleColumns = columns.filter((col) => col.sortFieldName);

        let mappedMultiSortMeta: I.DataTableSortMeta[] = multiSortMeta;

        if (filteredVisibleColumns.length > 0) {
            mappedMultiSortMeta = multiSortMeta.map((sortCol) => {
                const visibleCol = filteredVisibleColumns.find((visibleCol) => visibleCol.field == sortCol.field);
                return { ...sortCol, field: visibleCol ? (visibleCol?.sortFieldName as string) : sortCol?.field };
            });
        }

        return mappedMultiSortMeta;
    };

    const handleFilterChange = useCallback(
        (input: I.IPrimeFilterInput, filterType: FI.FilteringType) => {
            setParams((prev) => {
                let filtersCopy = deepCopy(prev.filters);
                const multiSortMetaCopy = deepCopy(prev.multiSortMeta);
                let { field, filterField, value } = input;

                if (filterField.includes(".")) filterField = H.convertDotsToUnderscores(filterField);

                if (!!value) {
                    if (!filtersCopy.some((filter) => filter.filterField == filterField)) {
                        filtersCopy = [...filtersCopy, { id: field, value, filterType, filterField, field, operator: "AND" }];
                    } else {
                        filtersCopy = filtersCopy.map((filter) =>
                            filter.filterField == filterField ? { ...filter, value, filterType } : filter
                        );
                    }
                } else {
                    filtersCopy = filtersCopy.filter((filter) => filter.filterField != filterField);
                }

                let urlParams: string = "";

                urlParams = H.generateUrlParams({
                    rows: paginationMeta?.rows,
                    multiSortMeta: mapMultiSortMeta(multiSortMetaCopy),
                    filters: filtersCopy,
                    additionalParams: externalCustomParam,
                });
                onReload && onReload(urlParams); // triggers dataReload
                onParamsChange && onParamsChange({ ...params, filters: filtersCopy });

                return { ...prev, filters: filtersCopy };
            });
        },
        [onReload]
    );

    const handleDateFilterChange = useCallback(
        (input: I.IPrimeFilterInput, filterType: string) => {
            setParams((prev) => {
                let filtersCopy = deepCopy(prev.filters);
                let { filterField, field, value } = input;

                if (filterField.includes(".")) filterField = H.convertDotsToUnderscores(filterField);

                if (!!value) {
                    if (!filtersCopy.some((filter) => filter.id == input.filterField)) {
                        filtersCopy = [...filtersCopy, { id: field, value, filterType, filterField, field, operator: "AND" }];
                    } else {
                        filtersCopy = filtersCopy.map((filter) => (filter.id == filterField ? { ...filter, value } : filter));
                    }
                } else {
                    filtersCopy = filtersCopy.filter((filter) => filter.id != filterField);
                }

                return { ...prev, filters: filtersCopy };
            });
        },
        [params.filters, params.multiSortMeta, JSON.stringify(higherDependencyId), ...refreshCallBack]
    );

    const handleCalendarClose = useCallback(
        (name: string) => {
            let { filters, multiSortMeta } = params;
            if (!filters || !filters.length || !paginationMeta) return;

            const { page, rows } = paginationMeta;

            const urlParams = H.generateUrlParams({
                page,
                rows,
                multiSortMeta: mapMultiSortMeta(multiSortMeta),
                filters,
                additionalParams: externalCustomParam,
            });

            onReload && onReload(urlParams); // triggers dataReload
            onParamsChange && onParamsChange(params);
        },
        [
            params.filters,
            params.multiSortMeta,
            paginationMeta.page,
            paginationMeta.rows,
            JSON.stringify(higherDependencyId),
            ...refreshCallBack,
        ]
    );

    const handleApplyAdvancedFilters = (filters: IFilter[], externalCustomParam?: string) => {
        let urlParams: string = H.generateUrlParams({
            rows: paginationMeta?.rows,
            multiSortMeta: mapMultiSortMeta(params.multiSortMeta),
            filters: filters,
            additionalParams: externalCustomParam,
        });

        setParams({ ...params, filters });
        onReload && onReload(urlParams); // triggers dataReload
    };

    const handleEditSubmit = async (submitEvent: I.EditSubmitEventType) =>
        applySubmit({
            submitEvent,
            firstPartEditUrl,
            customHandleEditSubmit,
            extraEditParams,
            params,
            setData,
            hideSuccessToast,
            idSelector,
            onEdit,
            onReload,
            handleCustomResponse,
        });

    const handleAdditionalColumnEditSubmit = async (submitEvent: I.EditSubmitEventType) => {
        applySubmit({
            submitEvent,
            firstPartEditUrl,
            customHandleEditSubmit,
            extraEditParams,
            params,
            setData,
            hideSuccessToast,
            isAdditionalColumn: true,
            idSelector,
            meta,
            onEdit,
            onReload,
            handleCustomResponse,
        });
    };

    const handleFocusSelectRecord = (row) => {
        if (onSelect) onSelect([row]);
    };

    const handleHideColumns = (columnFields: I.ColumnFieldType[] | I.ColumnFieldType) => {
        let selectedVisibleColumns = columnFields;

        if (typeof columnFields === "string")
            selectedVisibleColumns = generatedColumns.visible
                .filter((visibleCol) => !columnFields.includes(visibleCol.field))
                .map((filteredCol) => filteredCol.field);

        const columns = H.getVisibleAndHiddenColumns(
            generatedColumns.default,
            generatedColumns.visible,
            selectedVisibleColumns as string[]
        );

        const mappedColumns = H.mapColumnsWidth(columns.visibleColumns, tableContainerRef);
        setGeneratedColumns({ ...generatedColumns, visible: mappedColumns });

        if (!dynamicColumns) handleSaveTableConfig({ type: "columns", payload: { columns: mappedColumns } }); // saves columns change 1in config
        if (H.shouldClearParamsAndRefresh(columns.hiddenColumns, params)) {
            const { page, rows } = paginationMeta;
            const urlParams = H.generateUrlParams({ page, rows });

            setParams({ filters: [], multiSortMeta: undefined });
            onReload && onReload(urlParams); // triggers dataReload
        }
    };

    const handleGroupBy = (columnField?: I.ColumnFieldType) => {
        const groupByColumn = getColumnToGroupBy(
            generatedColumns.visible.filter((visibleCol) => visibleCol.sortable),
            columnField || undefined
        );

        let multiSortMeta: I.DataTableMultiSortMetaType = [];

        if (groupByColumn) multiSortMeta = [{ header: groupByColumn.header, field: groupByColumn.field, order: 1, groupByThisField: true }];
        handleSort({ multiSortMeta }, true);
    };

    const handleRestoreColumns = async () => {
        if (!(await sweetConfirm("Restore default columns", "This operation is not reversible."))) return;

        const additionalColumns: I.IPrimeTableColumn[] = await H.mapToAdditionalColumns(meta?.ExtraFields, additionalColumnsEnabled);

        const _columns = H.mapColumnsOnInit(tableContainerRef, [...columns, ...additionalColumns], t, minimumColWidth);
        handleTableConfig(_columns);

        setGeneratedColumns({ ...generatedColumns, visible: _columns, default: _columns });

        !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: _columns } }); // saves columns change  in config
    };

    const handleToggleFilters = () => {
        const _filterEnabled = !tableConfig.filtersEnabled;
        const tableHeight = tableConfig.tableHeight;

        let filterHeight: number = parseInt(H.getCssVariable(tableContainerRef, "--header-height"));
        const newTableHeight: number = _filterEnabled ? tableHeight - filterHeight : tableHeight + filterHeight;

        // H.setCssVariable(tableContainerRef, "--table-min-height", `${newTableHeight}px`);
        handleScrollConfig("vertical", newTableHeight, _filterEnabled);
        setTableConfig({ ...tableConfig, tableHeight: newTableHeight, filtersEnabled: _filterEnabled });

        !dynamicColumns && handleSaveTableConfig({ type: "filterEnabled", payload: { filterEnabled: _filterEnabled } }); // saves filterEnabled change in config
    };

    const handleFreezeColumn = (columnField?: I.ColumnFieldType) => {
        generatedColumns.visible.some((frozenColumn, originalPosition) => {
            if (frozenColumn.field == columnField)
                setTableConfig((prev) => ({
                    ...prev,
                    frozenColumn: {
                        field: frozenColumn.field,
                        width: H.getFrozenColWidth(frozenColumn),
                        originalPosition: originalPosition,
                    },
                }));
        });
        if (!columnField) setTableConfig((prev) => ({ ...prev, frozenColumn: undefined }));
    };

    const handleColumnResizeEnd = useCallback(
        (event: DataTableColumnResizeEndParams) => {
            // True if column that is resized is also frozen
            if (tableConfig.frozenColumn && tableConfig.frozenColumn.field == event.column.field) {
                const frozenColumn = { ...tableConfig.frozenColumn, width: `${parseInt(tableConfig.frozenColumn?.width) + event.delta}px` };
                setTableConfig({ ...tableConfig, frozenColumn: frozenColumn });
            } else {
                const resizedColumns = H.handleColumnResize(generatedColumns.visible, event, tableContainerRef, tableConfig.frozenColumn);
                !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: resizedColumns } }); // saves columns change in config
                setGeneratedColumns({ ...generatedColumns, visible: resizedColumns });
            }
        },
        [JSON.stringify(generatedColumns), tableConfig]
    );

    const handleColReorder = (event: DataTableColReorderParams) => {
        const reorderedColumns = H.getReorderedColumns(event, generatedColumns.visible);
        !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: reorderedColumns } }); // saves columns change in config

        setGeneratedColumns({ ...generatedColumns, visible: reorderedColumns });
    };

    const handleAdditionalColumns = async (event: I.HandleAdditionalColumnsEvent) => {
        // maps old visible columns width back to number type, without px unit at the and, thanks to this, user column width will not be lost while generating new columns
        let newColumns: I.IPrimeTableColumn[] = generatedColumns.visible.map((col) => ({ ...col, width: parseFloat(col.width) }));

        switch (event.action) {
            case I.ADDITIONAL_COLUMNS_EVENT_ACTION.ADD:
                if (!event.payload.selectedContextColumnField) {
                    newColumns.push(event.payload.column);
                } else {
                    const newColumnAddIndex: number =
                        newColumns.findIndex((col) => col.field === event.payload.selectedContextColumnField) + 1;
                    newColumns.splice(newColumnAddIndex, 0, event.payload.column);
                }

                break;
            case I.ADDITIONAL_COLUMNS_EVENT_ACTION.EDIT:
                const editColumnIndex = newColumns.findIndex((col) => col.field === event.payload.column.field);

                if (editColumnIndex !== -1) newColumns[editColumnIndex] = event.payload.column;
                break;
            case I.ADDITIONAL_COLUMNS_EVENT_ACTION.DELETE:
                newColumns = newColumns.filter((col) => col.field != event.payload.extraField);
                break;
        }

        // generates new columns(but if user changed width to some columns, new width will not be changed)
        const _columns = H.mapColumnsOnInit(tableContainerRef, newColumns, t, minimumColWidth);

        // refreshing table config with new columns
        handleTableConfig(_columns);

        // saving new columns in state
        setGeneratedColumns({ ...generatedColumns, visible: _columns, default: _columns });

        // saving new columns in db
        !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: _columns } });

        // refreshes whole data, this refreshes meta data and our additional columns (extra fields), which we need for context menu to work properly
        onReload && onReload(undefined, true);
    };

    const handleSelectedContextRecord = (e) => {
        if (onSelect && e.value?.[idSelector]) onSelect([e.value]);
    };

    const metaGeneratedAuditData = useMemo(
        () =>
            meta?.ModelInfo?.app && meta?.ModelInfo?.model ? { appLabel: meta?.ModelInfo?.app, model: meta?.ModelInfo?.model } : undefined,
        [meta]
    );

    const tableRef = useRef<HTMLDivElement>(null);

    const { scrollHeight } = useTableScrollHeight(tableRef, tableConfig.filtersEnabled, children, paginationMeta, adjustTableHeight);

    const scrollHeightAdjusted = scrollHeight + adjustTableHeight;

    const [virtualRows, setVirtualRows] = useState<{ first: number; rows: number }>({ first: 0, rows: 20 });

    const handleLoadVirtualRows = (e) => {
        setVirtualRows(e);
    };
    const componentRef = useRef<any>(null);

    return (
        <AdvancedRemarksContextProvider>
            <div className={getContainerHeight ? "gt-table" : ""} ref={tableRef}>
                {/* advanced filters widget */}

                <div className={classnames("primetable-btn-container", btnContainerClassName)}>
                    {(children || advancedFiltersConfig) && (
                        <ResponsiveButtonsContainer disableButtons={isPending}>
                            {children}
                            {extraContent ? extraContent : <></>}
                        </ResponsiveButtonsContainer>
                    )}

                    {!!advancedFiltersConfig && (
                        <AdvanceFiltersWrapper
                            visible={advancedFilterVisible}
                            onClose={handleCloseAdvancedFilters}
                            onApplyFilters={handleApplyAdvancedFilters}
                            toggleFilters={toggleAdvancedFilters}
                            tableRef={tableRef}
                            tableFilters={params.filters}
                            additionalParams={externalCustomParam}
                            advancedFiltersConfig={advancedFiltersConfig as I.IAdvancedFilterConfig[]}
                            selectChoices={selectChoices}
                        />
                    )}
                </div>
                <PrimeContextMenu
                    // Props needed for context-menu
                    contextMenuRef={contextMenuRef}
                    contextMenuItems={contextMenuItems}
                    // Props that provide data
                    auditData={auditData ?? metaGeneratedAuditData}
                    selectedRecord={dataSet?.find((row) => row?.[idSelector] == selectedRecords?.[0]?.[idSelector])}
                    dataSet={dataSet}
                    multiSort={params.multiSortMeta?.[0]}
                    tableConfig={tableConfig}
                    meta={meta}
                    tableName={tableName}
                    selectChoices={selectChoices}
                    // Enabled/disabled
                    enabledFunctionalities={{
                        exportCSVEnabled: exportCSVEnabled && dataSet.length > 0,
                        hidingColumnEnabled: hidingColumnEnabled,
                        groupRowsEnabled: tableEnabledFunctionality.groupingEnabled,
                        filterEnabled: filterEnabled && tableConfig.contextItemFilterEnable,
                        frozenColumnEnabled: (frozenColumnEnabled && scrollConfig?.horizontal) || false,
                        translateDisabled: translateDisabled,
                        additionalColumnsEnabled: additionalColumnsEnabled,
                        roleFilter,
                    }}
                    // Columns
                    defaultColumns={generatedColumns.default}
                    visibleColumns={generatedColumns.visible}
                    // handlers
                    handlers={{
                        handleRestoreColumns,
                        handleHideColumns,
                        handleToggleFilters,
                        handleFreezeColumn,
                        handleGroupBy,
                        handleContextColumnFieldChange: onContextColumnFieldChange,
                        handleAdditionalColumns,
                    }}
                />
                <div
                    ref={tableContainerRef}
                    className={`${className} prime-table-container`}
                    style={{ "--table-min-height": `${scrollHeightAdjusted}px` } as React.CSSProperties}
                    onContextMenu={(e) => contextMenuRef?.current && contextMenuRef.current.show(e)}
                >
                    <DataTable
                        ref={componentRef}
                        value={virtualScroll ? dataSet.slice(virtualRows.first, virtualRows.first + virtualRows.rows) : dataSet}
                        //virtualization
                        {...(virtualScroll && {
                            virtualRowHeight: 31,
                            virtualScroll: true,
                            onVirtualScroll: handleLoadVirtualRows,
                            lazy: true,
                            totalRecords: dataSet.length,
                            rows: 40,
                        })}
                        // Display and styles
                        className={`p-datatable-sm ${tableClassName}`}
                        showGridlines={showGridlines}
                        scrollHeight={"inherit"}
                        scrollable={scrollConfig?.horizontal || scrollConfig?.vertical}
                        emptyMessage={!(isRefreshing || isLoading) ? <EmptyMessage tableRef={tableRef} /> : <LoadingMessage />}
                        loading={isPending}
                        loadingIcon={""}
                        frozenWidth={tableConfig.frozenColumn?.width}
                        // Column reorder
                        reorderableColumns={reorderColumnsEnabled}
                        onColReorder={handleColReorder}
                        // Column resize
                        resizableColumns={resizableColumnsEnabled && generatedColumns.visible.length > 1}
                        columnResizeMode="expand"
                        onColumnResizeEnd={handleColumnResizeEnd}
                        // Context Menu
                        contextMenuSelection={selectedRecords?.[0]}
                        onContextMenuSelectionChange={handleSelectedContextRecord}
                        onContextMenu={(e) => contextMenuRef?.current && contextMenuRef.current.show(e.originalEvent)}
                        rowClassName={rowClassName ? (data) => rowClassName(data) : undefined}
                        // Selection
                        {...(Array.isArray(selectedRecords) &&
                            onSelect && {
                                compareSelectionBy: "equals",
                                selection: selectedRecords,
                                onSelectionChange: (selected) => {
                                    if (!isBusy) {
                                        if (notSelectFirst) onSelect(selected.value);
                                        else H.handleSelection(selected, dataSet, selectedRecords, onSelect, idSelector);
                                    }
                                },
                                selectionMode: selectionMode,
                                metaKeySelection,
                            })}
                        // Server side pagination
                        {...(tableEnabledFunctionality.pagination && {
                            paginator: true,
                            first: paginationMeta.first,
                            rows: paginationMeta.rows,
                            totalRecords: paginationMeta.totalRecords,
                            rowsPerPageOptions: rowsPerPage,
                            onPage: handlePage,
                            lazy: true,
                            paginatorTemplate: tablePaginator,
                        })}
                        // Grouping
                        {...(tableEnabledFunctionality.groupingSelected && {
                            rowGroupMode: "subheader",
                            groupField: params.multiSortMeta?.[0].field,
                            rowGroupHeaderTemplate: customRowGroupHeader
                                ? customRowGroupHeader
                                : (row) => (
                                      <MemoRowGroupHeader
                                          frozenColumn={!!tableConfig.frozenColumn}
                                          row={row}
                                          groupField={params.multiSortMeta?.[0]}
                                          t={t}
                                      />
                                  ),
                            rowGroupFooterTemplate: customRowGroupFooter ? customRowGroupFooter : () => <td />,
                        })}
                        // Group and sort
                        {...(tableEnabledFunctionality.sortable && {
                            onSort: handleSort,
                            removableSort: true,
                            sortMode: "multiple",
                            multiSortMeta: params?.multiSortMeta,
                        })}
                        onRowDoubleClick={(e) => handleDoubleClick && handleDoubleClick(e)}
                    >
                        {generatedColumns.visible.map((col) =>
                            H.getDynamicColumns<T>({
                                props,
                                column: col,
                                args: {
                                    tableConfig,
                                    params,
                                    filterFocusedFieldName,
                                    tableContainerRef,
                                    isBusy,
                                    setFilterFocusedFieldName,
                                    handleFocusSelectRecord,
                                    handleAdditionalColumnEditSubmit,
                                    handleEditSubmit,
                                    handleFilterChange,
                                    handleCalendarClose,
                                    handleDateFilterChange,
                                    t,
                                },
                            })
                        )}
                    </DataTable>
                </div>
                <AdvancedRemarksPopup modelName={advancedRemarksModel} onReload={onReload} />
            </div>
        </AdvancedRemarksContextProvider>
    );
};

export const PrimeDataTable = forwardRef(PrimeDataTableInner) as <T extends I.IBaseRecord = I.IBaseRecord>(
    props: I.IPrimeDataTable<T>
) => ReactElement;

const arePropsEqual = (prev, next) => {
    return (
        JSON.stringify(prev.dataSet) == JSON.stringify(next.dataSet) &&
        JSON.stringify(prev.selectedRecords) == JSON.stringify(next.selectedRecords) &&
        JSON.stringify(prev.meta) == JSON.stringify(next.meta) &&
        JSON.stringify(prev.columns) == JSON.stringify(next.columns)
    );
};

// export const PrimeDataTable = memo(PrimeDataTableBase, arePropsEqual) as typeof PrimeDataTableBase;
